import { useState } from 'react';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { facturesAvecAcompteAnterieur } from '../../lib/acomptes-anterieurs';
import { anneeDe } from '../../lib/cloture';
import { DEVISE, fmt } from '../../lib/currency';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

/**
 * Compta → Synthèse : les factures dont un acompte a été versé l'année
 * d'avant (« Paiements reçus »), et dans quelle année les compter.
 *
 * <p>Un acompte de 7'807.– reçu le 24.12.2025 pour une salle de bain
 * facturée en 2026 : la société le compte en 2025, année du versement, ou en
 * 2026, année des travaux — il figure alors au bilan 2025 comme acompte reçu
 * d'avance. Les deux sont admis, et le patron choisit pour chaque année de
 * versement : « ceux de 2025, je les ai déclarés en 2025 ; ceux reçus en 2026
 * pour 2027, je déciderai peut-être 2027 ».
 *
 * @param {object} props propriétés
 * @param {object[]} props.factures toutes les factures
 * @param {Map<number, {date: string, montant: number}[]>} props.paiements paiements par facture
 * @param {(annee: number) => 'encaisse'|'travaux'} props.regle règle de chaque année de versement
 * @param {(annee: number, regle: 'encaisse'|'travaux') => void} props.onRegle choisit la règle d'une année
 * @param {object[]} props.clients clients, pour les noms
 * @param {number} props.annee exercice affiché
 * @returns {import('react').ReactElement|null} la carte, ou rien sans facture concernée
 */
export function AcomptesAnterieurs({ factures, paiements, regle, onRegle, clients, annee }) {
  const regleDe = typeof regle === 'function' ? regle : () => regle;
  const concernees = facturesAvecAcompteAnterieur(factures, paiements).filter(
    ({ facture, avant }) =>
      anneeDe(facture) === annee || avant.some((p) => anneeDe({ date: p.date }) === annee),
  );
  // Le choix s'enregistre au toucher, sans bouton « Enregistrer » : sans
  // signe, toucher le bouton déjà choisi semblait ne rien faire.
  const [enregistre, setEnregistre] = useState(null);
  if (!concernees.length) return null;
  const nom = (id) => (clients || []).find((c) => c.id === id)?.nom || '';
  // Une rangée de choix par année de versement, la plus ancienne d'abord.
  const anneesVersement = [
    ...new Set(concernees.flatMap(({ avant }) => avant.map((p) => anneeDe({ date: p.date })))),
  ].sort((a, b) => a - b);
  const bouton = (an, cle, libelle) => {
    const choisi = regleDe(an) === cle;
    return (
      <button
        key={cle}
        type="button"
        onClick={() => {
          onRegle(an, cle);
          setEnregistre(an);
        }}
        style={{
          flex: 1,
          padding: '9px 6px',
          borderRadius: 9,
          border: '1px solid ' + (choisi ? C.navy : C.border),
          background: choisi ? C.navy : C.white,
          color: choisi ? '#fff' : C.grey,
          fontSize: 10.5,
          fontWeight: 800,
          cursor: 'pointer',
        }}
      >
        {choisi ? '✓ ' : ''}
        {libelle}
      </button>
    );
  };
  return (
    <Card style={{ marginBottom: 11 }}>
      <Bar
        icon="📅"
        title={tr('Acomptes versés l’année d’avant')}
        color={C.amber}
        sub={tr('Dans quelle année les compter ?')}
      />
      <div style={{ padding: 13 }}>
        {anneesVersement.map((an) => (
          <div key={an} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: C.ink, marginBottom: 6 }}>
              {trf('Acomptes reçus en {0}', an)}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {bouton(an, 'encaisse', tr('Année du versement'))}
              {bouton(an, 'travaux', tr('Année des travaux'))}
            </div>
            {enregistre === an && (
              <div
                role="status"
                style={{ fontSize: 10.5, fontWeight: 800, color: C.green, marginTop: 6 }}
              >
                {tr('✓ Enregistré !')}
              </div>
            )}
          </div>
        ))}
        <div style={{ fontSize: 9.5, color: C.grey, lineHeight: 1.5, marginBottom: 8 }}>
          {tr('L’acompte compte dans l’année où l’argent est arrivé ; la facture garde le reste.')}{' '}
          {tr('Les deux sont admis : tu choisis pour chaque année, avec ta fiduciaire.')}
        </div>
        {concernees.map(({ facture, avant }) => (
          <div
            key={facture.id}
            style={{
              borderTop: '1px solid ' + C.border,
              padding: '7px 0',
              fontSize: 11,
            }}
          >
            <div style={{ fontWeight: 800 }}>
              {trf('Facture {0}', facture.num || facture.id)}
              {nom(facture.clientId) ? ' · ' + nom(facture.clientId) : ''}
              <span style={{ color: C.muted, fontWeight: 600 }}> · {facture.date}</span>
            </div>
            {avant.map((p) => {
              const an = anneeDe({ date: p.date });
              return (
                <div key={p.date + p.montant} style={{ color: C.grey }}>
                  {trf(
                    'Acompte {0} reçu le {1} → compté en {2}',
                    DEVISE + ' ' + fmt(p.montant),
                    p.date,
                    regleDe(an) === 'encaisse' ? an : anneeDe(facture),
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </Card>
  );
}
